import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
} from "@mui/material";
import { BarChart, PieChart } from "@mui/x-charts";
import dayjs from "../../lib/dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  getAppointmentsReport,
  AppointmentsReportResponse,
  AppointmentStatus,
  getPatientsReport,
  PatientsReportResponse,
  getDoctorsReport,
  DoctorsReportResponse,
} from "../../services/reports.service";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../router/constants";

const ALL_STATUSES: AppointmentStatus[] = [
  "PENDENTE",
  "CONFIRMADO",
  "CANCELADO",
  "CONCLUIDO",
  "PAGAMENTO_PENDENTE",
  "PAGAMENTO_PROCESADO",
];

type SortKey =
  | "date"
  | "status"
  | "patient"
  | "doctor"
  | "local"
  | "city"
  | "level"
  | "appointmentsCount"
  | "socialProgram"
  | "completedAppointmentsCount"
  | "availableHours";

type ReportType = "appointments" | "patients" | "doctors";

export function Reports() {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState<ReportType>("appointments");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [statuses, setStatuses] = useState<AppointmentStatus[]>([]);

  // Filtros de pacientes
  const [patientLevel, setPatientLevel] = useState<string>("");
  const [patientCity, setPatientCity] = useState<string>("");
  const [patientState, setPatientState] = useState<string>("");

  // Filtros de médicos
  const [doctorStartDate, setDoctorStartDate] = useState<string>("");
  const [doctorEndDate, setDoctorEndDate] = useState<string>("");
  const [doctorCreatedStartDate, setDoctorCreatedStartDate] = useState<string>("");
  const [doctorCreatedEndDate, setDoctorCreatedEndDate] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [appointmentsData, setAppointmentsData] = useState<AppointmentsReportResponse | null>(null);
  const [patientsData, setPatientsData] = useState<PatientsReportResponse | null>(null);
  const [doctorsData, setDoctorsData] = useState<DoctorsReportResponse | null>(null);

  const [orderBy, setOrderBy] = useState<SortKey>("date");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const [appointmentsPage, setAppointmentsPage] = useState(0);
  const [appointmentsRowsPerPage, setAppointmentsRowsPerPage] = useState(10);
  const [patientsPage, setPatientsPage] = useState(0);
  const [patientsRowsPerPage, setPatientsRowsPerPage] = useState(10);
  const [doctorsPage, setDoctorsPage] = useState(0);
  const [doctorsRowsPerPage, setDoctorsRowsPerPage] = useState(10);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (reportType === "appointments") {
        const res = await getAppointmentsReport({
          start: startDate || undefined,
          end: endDate || undefined,
          status: statuses.length ? statuses : undefined,
          page: appointmentsPage + 1,
          perPage: appointmentsRowsPerPage,
        });
        setAppointmentsData(res);
      } else if (reportType === "patients") {
        const res = await getPatientsReport({
          start: startDate || undefined,
          end: endDate || undefined,
          level: patientLevel || undefined,
          city: patientCity || undefined,
          state: patientState || undefined,
          page: patientsPage + 1,
          perPage: patientsRowsPerPage,
        });
        setPatientsData(res);
      } else if (reportType === "doctors") {
        const res = await getDoctorsReport({
          start: doctorStartDate || undefined,
          end: doctorEndDate || undefined,
          createdStart: doctorCreatedStartDate || undefined,
          createdEnd: doctorCreatedEndDate || undefined,
          page: doctorsPage + 1,
          perPage: doctorsRowsPerPage,
        });
        setDoctorsData(res);
      }
    } catch (e: any) {
      console.error(e);
      setError("Não foi possível carregar o relatório. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [
    reportType,
    startDate,
    endDate,
    statuses,
    patientLevel,
    patientCity,
    patientState,
    doctorStartDate,
    doctorEndDate,
    doctorCreatedStartDate,
    doctorCreatedEndDate,
    appointmentsPage,
    appointmentsRowsPerPage,
    patientsPage,
    patientsRowsPerPage,
    doctorsPage,
    doctorsRowsPerPage,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApplyFilters = () => {
    // ao aplicar filtros, voltamos para a primeira página do relatório atual
    if (reportType === "appointments") {
      setAppointmentsPage(0);
    } else if (reportType === "patients") {
      setPatientsPage(0);
    } else if (reportType === "doctors") {
      setDoctorsPage(0);
    }
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setStatuses([]);
    setPatientLevel("");
    setPatientCity("");
    setPatientState("");
    setDoctorStartDate("");
    setDoctorEndDate("");
    setDoctorCreatedStartDate("");
    setDoctorCreatedEndDate("");

    setAppointmentsPage(0);
    setPatientsPage(0);
    setDoctorsPage(0);
  };

  const handleStatusChange = (event: any) => {
    // Para Select multiple do MUI, o value já vem como array (quando não é native)
    const value = event.target.value as AppointmentStatus[];
    setStatuses(value || []);
  };

  const handleChangeReportType = (event: any) => {
    const value = event.target.value as ReportType;
    setReportType(value);
    setOrderBy("date");
    setOrder("asc");
    // ao trocar de tipo de relatório, começamos sempre da primeira página
    setAppointmentsPage(0);
    setPatientsPage(0);
    setDoctorsPage(0);
  };

  const formatDateTime = (iso: string) => {
    return dayjs(iso).format("DD/MM/YYYY HH:mm");
  };

  const getStatusColor = (status: string): "default" | "warning" | "success" | "error" => {
    switch (status) {
      case "PENDENTE":
      case "PAGAMENTO_PENDENTE":
        return "warning";
      case "CONFIRMADO":
      case "CONCLUIDO":
      case "PAGAMENTO_PROCESADO":
        return "success";
      case "CANCELADO":
        return "error";
      default:
        return "default";
    }
  };

  const handleRequestSort = (key: SortKey) => {
    setOrder((prevOrder) => (orderBy === key && prevOrder === "asc" ? "desc" : "asc"));
    setOrderBy(key);
  };

  const handleChangeAppointmentsPage = (_: unknown, newPage: number) => {
    setAppointmentsPage(newPage);
  };

  const handleChangeAppointmentsRowsPerPage = (event: any) => {
    const newPerPage = parseInt(event.target.value, 10);
    setAppointmentsRowsPerPage(newPerPage);
    setAppointmentsPage(0);
  };

  const handleChangePatientsPage = (_: unknown, newPage: number) => {
    setPatientsPage(newPage);
  };

  const handleChangePatientsRowsPerPage = (event: any) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPatientsRowsPerPage(newPerPage);
    setPatientsPage(0);
  };

  const handleChangeDoctorsPage = (_: unknown, newPage: number) => {
    setDoctorsPage(newPage);
  };

  const handleChangeDoctorsRowsPerPage = (event: any) => {
    const newPerPage = parseInt(event.target.value, 10);
    setDoctorsRowsPerPage(newPerPage);
    setDoctorsPage(0);
  };

  const paginationRowsPerPageOptions = [10, 25, 50, 100];

  const getPaginationLabelDisplayedRows = ({ from, to, count }: any) => {
    const total = count !== -1 ? count : `mais de ${to}`;
    return `${from}-${to} de ${total}`;
  };

  const sortedAppointments = useMemo(() => {
    if (!appointmentsData) return [];

    const items = [...appointmentsData.appointments];

    items.sort((a, b) => {
      let aValue: string | number = "";
      let bValue: string | number = "";

      switch (orderBy) {
        case "date":
          aValue = dayjs(a.date).valueOf();
          bValue = dayjs(b.date).valueOf();
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        case "patient":
          aValue = a.patient?.name || "";
          bValue = b.patient?.name || "";
          break;
        case "doctor":
          aValue = a.doctor?.name || "";
          bValue = b.doctor?.name || "";
          break;
        case "local":
          aValue = a.local?.localName || "";
          bValue = b.local?.localName || "";
          break;
        case "city":
          aValue = a.local?.city || "";
          bValue = b.local?.city || "";
          break;
        default:
          break;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return order === "asc" ? -1 : 1;
      if (aStr > bStr) return order === "asc" ? 1 : -1;
      return 0;
    });

    return items;
  }, [appointmentsData, orderBy, order]);

  const appointmentsChartsData = useMemo(() => {
    if (!appointmentsData) {
      return {
        totalAppointments: 0,
        totalAvailableHours: 0,
        pendingCount: 0,
        confirmedCount: 0,
        cities: [] as { city: string; count: number }[],
      };
    }

    const totalAppointments = appointmentsData.totals.appointments ?? 0;
    const totalAvailableHours = appointmentsData.totals.availableHours ?? 0;

    let pendingCount = 0;
    let confirmedCount = 0;
    const cityCounter: Record<string, number> = {};

    for (const appt of appointmentsData.appointments) {
      if (appt.status === "PENDENTE") pendingCount += 1;
      if (appt.status === "CONFIRMADO") confirmedCount += 1;

      const rawCity = appt.local?.city?.trim();
      const rawState = appt.local?.state?.trim();
      const city = rawCity || "Não informado";
      const label = rawState && city !== "Não informado" ? `${city} / ${rawState}` : city;

      cityCounter[label] = (cityCounter[label] || 0) + 1;
    }

    const cities = Object.entries(cityCounter)
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalAppointments,
      totalAvailableHours,
      pendingCount,
      confirmedCount,
      cities,
    };
  }, [appointmentsData]);

  const APPOINTMENTS_CITY_BAR_HEIGHT = 18;
  const appointmentsCitiesChartHeight = Math.max(
    80,
    appointmentsChartsData.cities.length * APPOINTMENTS_CITY_BAR_HEIGHT + 30,
  );

  const patientsChartsData = useMemo(() => {
    if (!patientsData) {
      return {
        totalPatients: 0,
        socialProgramCount: 0,
        nonSocialProgramCount: 0,
        withAppointmentsCount: 0,
        withoutAppointmentsCount: 0,
        levels: [] as { level: string; count: number }[],
      };
    }

    const totalPatients = patientsData.totals.patients ?? 0;
    const socialProgramCount = patientsData.totals.socialProgram ?? 0;
    const nonSocialProgramCount = Math.max(totalPatients - socialProgramCount, 0);

    const withAppointmentsCount = patientsData.totals.withAppointments ?? 0;
    const withoutAppointmentsCount = Math.max(
      totalPatients - withAppointmentsCount,
      0,
    );

    const levelCounter: Record<string, number> = {};
    for (const p of patientsData.patients) {
      const key = p.level != null ? String(p.level) : "Sem nível";
      levelCounter[key] = (levelCounter[key] || 0) + 1;
    }

    const levels = Object.entries(levelCounter)
      .map(([level, count]) => ({ level, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalPatients,
      socialProgramCount,
      nonSocialProgramCount,
      withAppointmentsCount,
      withoutAppointmentsCount,
      levels,
    };
  }, [patientsData]);

  const PATIENTS_LEVEL_BAR_HEIGHT = 18;
  const patientsLevelsChartHeight = Math.max(
    80,
    patientsChartsData.levels.length * PATIENTS_LEVEL_BAR_HEIGHT + 30,
  );

  const sortedPatients = useMemo(() => {
    if (!patientsData) return [];

    const items = [...patientsData.patients];

    items.sort((a, b) => {
      let aValue: string | number | boolean = "";
      let bValue: string | number | boolean = "";

      switch (orderBy) {
        case "date":
          aValue = dayjs(a.createdAt).valueOf();
          bValue = dayjs(b.createdAt).valueOf();
          break;
        case "patient":
          aValue = a.name || "";
          bValue = b.name || "";
          break;
        case "city":
          aValue = `${a.city || ""} ${a.state || ""}`.trim();
          bValue = `${b.city || ""} ${b.state || ""}`.trim();
          break;
        case "level":
          aValue = a.level ?? -1;
          bValue = b.level ?? -1;
          break;
        case "appointmentsCount":
          aValue = a.appointmentsCount;
          bValue = b.appointmentsCount;
          break;
        case "socialProgram":
          aValue = a.socialProgram ?? false;
          bValue = b.socialProgram ?? false;
          break;
        default:
          break;
      }

      // números
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      // booleanos
      if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        if (aValue === bValue) return 0;
        return order === "asc"
          ? aValue
            ? -1
            : 1
          : aValue
          ? 1
          : -1;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return order === "asc" ? -1 : 1;
      if (aStr > bStr) return order === "asc" ? 1 : -1;
      return 0;
    });

    return items;
  }, [patientsData, orderBy, order]);

  const sortedDoctors = useMemo(() => {
    if (!doctorsData) return [];

    const items = [...doctorsData.doctors];

    items.sort((a, b) => {
      let aValue: string | number = "";
      let bValue: string | number = "";

      switch (orderBy) {
        case "date":
          aValue = dayjs(a.createdAt).valueOf();
          bValue = dayjs(b.createdAt).valueOf();
          break;
        case "doctor":
          aValue = a.name || "";
          bValue = b.name || "";
          break;
        case "city": {
          const aClinic = a.clinics[0];
          const bClinic = b.clinics[0];
          const aStr = aClinic ? `${aClinic.city || ""} ${aClinic.state || ""}`.trim() : "";
          const bStr = bClinic ? `${bClinic.city || ""} ${bClinic.state || ""}`.trim() : "";
          aValue = aStr;
          bValue = bStr;
          break;
        }
        case "appointmentsCount":
          aValue = a.appointmentsCount;
          bValue = b.appointmentsCount;
          break;
        case "completedAppointmentsCount":
          aValue = a.completedAppointmentsCount;
          bValue = b.completedAppointmentsCount;
          break;
        case "availableHours":
          aValue = a.availableHours;
          bValue = b.availableHours;
          break;
        default:
          break;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return order === "asc" ? -1 : 1;
      if (aStr > bStr) return order === "asc" ? 1 : -1;
      return 0;
    });

    return items;
  }, [doctorsData, orderBy, order]);

  const doctorsChartsData = useMemo(() => {
    if (!doctorsData) {
      return {
        totalAppointments: 0,
        completedAppointments: 0,
        pendingOrOtherAppointments: 0,
        availableHours: 0,
        doctorsByAppointments: [] as { doctor: string; count: number }[],
      };
    }

    const totalAppointments = doctorsData.totals.appointments ?? 0;
    const completedAppointments = doctorsData.totals.completedAppointments ?? 0;
    const pendingOrOtherAppointments = Math.max(
      totalAppointments - completedAppointments,
      0,
    );
    const availableHours = doctorsData.totals.availableHours ?? 0;

    const doctorsByAppointments = [...doctorsData.doctors]
      .map((d) => ({ doctor: d.name, count: d.appointmentsCount }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalAppointments,
      completedAppointments,
      pendingOrOtherAppointments,
      availableHours,
      doctorsByAppointments,
    };
  }, [doctorsData]);

  const DOCTORS_TOP_BAR_HEIGHT = 18;
  const doctorsTopChartHeight = Math.max(
    80,
    doctorsChartsData.doctorsByAppointments.length * DOCTORS_TOP_BAR_HEIGHT + 30,
  );

  type ExportFormat = "pdf" | "excel";

  const exportAppointmentsReport = async (format: ExportFormat) => {
    if (!appointmentsData) return;

    const totalItems = appointmentsData.pagination?.totalItems ?? appointmentsData.appointments.length;

    const fullData = await getAppointmentsReport({
      start: startDate || undefined,
      end: endDate || undefined,
      status: statuses.length ? statuses : undefined,
      page: 1,
      perPage: totalItems,
    });

    const rows = fullData.appointments.map((appt) => [
      formatDateTime(appt.date),
      appt.status,
      appt.patient?.name || "-",
      appt.doctor?.name || "-",
      appt.local?.localName || "-",
      `${appt.local?.city || "-"}${appt.local?.state ? ` / ${appt.local.state}` : ""}`,
    ]);

    const totalsRows = [
      ["Total de agendamentos", fullData.totals.appointments],
      ["Pacientes distintos", fullData.totals.patients],
      ["Médicos distintos", fullData.totals.doctors],
      ["Horas disponíveis", fullData.totals.availableHours ?? "-"],
    ];

    const filenameBase = `relatorio-agendamentos-${dayjs().format("YYYYMMDD-HHmm")}`;

    if (format === "excel") {
      const wsData = [
        ["Totais"],
        ["Métrica", "Valor"],
        ...totalsRows,
        [],
        ["Data", "Status", "Paciente", "Médico", "Local", "Cidade / UF"],
        ...rows,
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Agendamentos");
      XLSX.writeFile(wb, `${filenameBase}.xlsx`);
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Relatório de Agendamentos", 14, 16);

    (doc as any).autoTable({
      startY: 22,
      head: [["Métrica", "Valor"]],
      body: totalsRows,
    });

    const finalY = ((doc as any).lastAutoTable?.finalY || 22) + 6;

    (doc as any).autoTable({
      startY: finalY,
      head: [["Data", "Status", "Paciente", "Médico", "Local", "Cidade / UF"]],
      body: rows,
    });

    doc.save(`${filenameBase}.pdf`);
  };

  const exportPatientsReport = async (format: ExportFormat) => {
    if (!patientsData) return;

    const totalItems = patientsData.pagination?.totalItems ?? patientsData.patients.length;

    const fullData = await getPatientsReport({
      start: startDate || undefined,
      end: endDate || undefined,
      level: patientLevel || undefined,
      city: patientCity || undefined,
      state: patientState || undefined,
      page: 1,
      perPage: totalItems,
    });

    const rows = fullData.patients.map((p) => [
      p.name,
      formatDateTime(p.createdAt),
      p.level ?? "-",
      p.socialProgram ? "Sim" : "Não",
      p.city || p.state ? `${p.city || ""}${p.state ? ` / ${p.state}` : ""}` : "-",
      p.appointmentsCount,
    ]);

    const totalsRows = [
      ["Pacientes cadastrados", fullData.totals.patients],
      ["Em programa social", fullData.totals.socialProgram],
      ["Com agendamentos", fullData.totals.withAppointments],
    ];

    const filenameBase = `relatorio-pacientes-${dayjs().format("YYYYMMDD-HHmm")}`;

    if (format === "excel") {
      const wsData = [
        ["Totais"],
        ["Métrica", "Valor"],
        ...totalsRows,
        [],
        ["Paciente", "Data de cadastro", "Nível", "Programa social", "Cidade / UF", "Consultas"],
        ...rows,
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Pacientes");
      XLSX.writeFile(wb, `${filenameBase}.xlsx`);
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Relatório de Pacientes", 14, 16);

    (doc as any).autoTable({
      startY: 22,
      head: [["Métrica", "Valor"]],
      body: totalsRows,
    });

    const finalY = ((doc as any).lastAutoTable?.finalY || 22) + 6;

    (doc as any).autoTable({
      startY: finalY,
      head: [["Paciente", "Data de cadastro", "Nível", "Programa social", "Cidade / UF", "Consultas"]],
      body: rows,
    });

    doc.save(`${filenameBase}.pdf`);
  };

  const exportDoctorsReport = async (format: ExportFormat) => {
    if (!doctorsData) return;

    const totalItems = doctorsData.pagination?.totalItems ?? doctorsData.doctors.length;

    const fullData = await getDoctorsReport({
      start: doctorStartDate || undefined,
      end: doctorEndDate || undefined,
      createdStart: doctorCreatedStartDate || undefined,
      createdEnd: doctorCreatedEndDate || undefined,
      page: 1,
      perPage: totalItems,
    });

    const rows = fullData.doctors.map((d) => {
      const mainClinic = d.clinics[0];
      const clinicsLabel = d.clinics.length === 0
        ? "-"
        : d.clinics.map((c) => c.localName || c.address || c.id).join(", ");
      const cityUfLabel = mainClinic
        ? `${mainClinic.city || ""}${mainClinic.state ? ` / ${mainClinic.state}` : ""}` || "-"
        : "-";

      return [
        d.name,
        formatDateTime(d.createdAt),
        clinicsLabel,
        cityUfLabel,
        d.appointmentsCount,
        d.completedAppointmentsCount,
        d.availableHours,
      ];
    });

    const totalsRows = [
      ["Médicos distintos", fullData.totals.doctors],
      ["Consultas no período", fullData.totals.appointments],
      ["Consultas concluídas", fullData.totals.completedAppointments],
      ["Horas disponíveis", fullData.totals.availableHours],
    ];

    const filenameBase = `relatorio-medicos-${dayjs().format("YYYYMMDD-HHmm")}`;

    if (format === "excel") {
      const wsData = [
        ["Totais"],
        ["Métrica", "Valor"],
        ...totalsRows,
        [],
        [
          "Médico",
          "Data de cadastro",
          "Clínicas",
          "Cidade / UF (principal)",
          "Consultas",
          "Concluídas",
          "Horas disponíveis",
        ],
        ...rows,
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Médicos");
      XLSX.writeFile(wb, `${filenameBase}.xlsx`);
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Relatório de Médicos", 14, 16);

    (doc as any).autoTable({
      startY: 22,
      head: [["Métrica", "Valor"]],
      body: totalsRows,
    });

    const finalY = ((doc as any).lastAutoTable?.finalY || 22) + 6;

    (doc as any).autoTable({
      startY: finalY,
      head: [[
        "Médico",
        "Data de cadastro",
        "Clínicas",
        "Cidade / UF (principal)",
        "Consultas",
        "Concluídas",
        "Horas disponíveis",
      ]],
      body: rows,
    });

    doc.save(`${filenameBase}.pdf`);
  };

  const handleExport = async (format: ExportFormat) => {
    try {
      setExporting(true);

      if (reportType === "appointments") {
        await exportAppointmentsReport(format);
      } else if (reportType === "patients") {
        await exportPatientsReport(format);
      } else if (reportType === "doctors") {
        await exportDoctorsReport(format);
      }
    } catch (e) {
      console.error("Erro ao exportar relatório", e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box
      width="100%"
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", mt: 6 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Relatórios
        </Typography>

        <Typography variant="body1" color="text.secondary" gutterBottom>
          Visualize relatórios da agenda, de pacientes e de médicos.
        </Typography>

        {/* Filtros */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Filtros
            </Typography>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mt: 1, mb: 2 }}
            >
              {/* Tipo de relatório */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="report-type-label">Tipo de relatório</InputLabel>
                <Select
                  labelId="report-type-label"
                  label="Tipo de relatório"
                  value={reportType}
                  onChange={handleChangeReportType}
                >
                  <MenuItem value="appointments">Agenda (agendamentos)</MenuItem>
                  <MenuItem value="patients">Pacientes</MenuItem>
                  <MenuItem value="doctors">Médicos</MenuItem>
                </Select>
              </FormControl>

              {reportType === "appointments" && (
                <>
                  <TextField
                    label="Data inicial"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="Data final"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      multiple
                      value={statuses}
                      label="Status"
                      onChange={handleStatusChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {ALL_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {reportType === "patients" && (
                <>
                  <TextField
                    label="Data de cadastro inicial"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="Data de cadastro final"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="Nível"
                    type="number"
                    size="small"
                    value={patientLevel}
                    onChange={(e) => setPatientLevel(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ maxWidth: 120 }}
                  />

                  <TextField
                    label="Cidade"
                    size="small"
                    value={patientCity}
                    onChange={(e) => setPatientCity(e.target.value)}
                  />

                  <TextField
                    label="UF"
                    size="small"
                    value={patientState}
                    onChange={(e) => setPatientState(e.target.value)}
                    sx={{ maxWidth: 80 }}
                  />
                </>
              )}

              {reportType === "doctors" && (
                <>
                  <TextField
                    label="Período - início"
                    type="date"
                    size="small"
                    value={doctorStartDate}
                    onChange={(e) => setDoctorStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="Período - fim"
                    type="date"
                    size="small"
                    value={doctorEndDate}
                    onChange={(e) => setDoctorEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="Cadastro - início"
                    type="date"
                    size="small"
                    value={doctorCreatedStartDate}
                    onChange={(e) => setDoctorCreatedStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="Cadastro - fim"
                    type="date"
                    size="small"
                    value={doctorCreatedEndDate}
                    onChange={(e) => setDoctorCreatedEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              )}
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => handleExport("pdf")}
                  disabled={exporting}
                >
                  Exportar PDF
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => handleExport("excel")}
                  disabled={exporting}
                >
                  Exportar Excel
                </Button>
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button variant="outlined" color="inherit" onClick={handleClearFilters}>
                  Limpar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : "Aplicar filtros"}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Cards de totais */}
        {reportType === "appointments" && appointmentsData && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total de agendamentos
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {appointmentsData.totals.appointments}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Pacientes distintos
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {appointmentsData.totals.patients}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Médicos distintos
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {appointmentsData.totals.doctors}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}

        {reportType === "appointments" &&
          appointmentsData &&
          appointmentsData.appointments.length > 0 && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Agendamentos x horas disponíveis
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <PieChart
                      width={110}
                      height={90}
                      series={[
                        {
                          innerRadius: 18,
                          outerRadius: 40,
                          paddingAngle: 2,
                          data: [
                            {
                              id: 0,
                              value: appointmentsChartsData.totalAppointments,
                              label: "Agendamentos",
                            },
                            {
                              id: 1,
                              value: appointmentsChartsData.totalAvailableHours,
                              label: "Horas disp.",
                            },
                          ],
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Pendentes x confirmados
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <PieChart
                      width={110}
                      height={90}
                      series={[
                        {
                          innerRadius: 18,
                          outerRadius: 40,
                          paddingAngle: 2,
                          data: [
                            {
                              id: 0,
                              value: appointmentsChartsData.pendingCount,
                              label: "Pendentes",
                            },
                            {
                              id: 1,
                              value: appointmentsChartsData.confirmedCount,
                              label: "Confirmados",
                            },
                          ],
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Agendamentos por cidade (top 5)
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <BarChart
                      width={240}
                      height={appointmentsCitiesChartHeight}
                      layout="horizontal"
                      xAxis={[
                        {
                          label: "Quantidade",
                          min: 0,
                        },
                      ]}
                      yAxis={[
                        {
                          scaleType: "band",
                          data: appointmentsChartsData.cities.map((c) => {
                            const base = c.city.split(" / ")[0];
                            return base.length > 15 ? `${base.slice(0, 15)}...` : base;
                          }),
                        },
                      ]}
                      series={[
                        {
                          data: appointmentsChartsData.cities.map((c) => c.count),
                          color: "#1976d2",
                        },
                      ]}
                      margin={{ top: 10, right: 10, bottom: 20, left: 80 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          )}

        {reportType === "patients" && patientsData && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Pacientes cadastrados
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {patientsData.totals.patients}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Em programa social
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {patientsData.totals.socialProgram}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Com agendamentos
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {patientsData.totals.withAppointments}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}

        {reportType === "patients" &&
          patientsData &&
          patientsData.patients.length > 0 && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Programa social
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <PieChart
                      width={110}
                      height={90}
                      series={[
                        {
                          innerRadius: 18,
                          outerRadius: 40,
                          paddingAngle: 2,
                          data: [
                            {
                              id: 0,
                              value: patientsChartsData.socialProgramCount,
                              label: "Em programa",
                            },
                            {
                              id: 1,
                              value: patientsChartsData.nonSocialProgramCount,
                              label: "Sem programa",
                            },
                          ],
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Com x sem agendamentos
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <PieChart
                      width={110}
                      height={90}
                      series={[
                        {
                          innerRadius: 18,
                          outerRadius: 40,
                          paddingAngle: 2,
                          data: [
                            {
                              id: 0,
                              value: patientsChartsData.withAppointmentsCount,
                              label: "Com agend.",
                            },
                            {
                              id: 1,
                              value: patientsChartsData.withoutAppointmentsCount,
                              label: "Sem agend.",
                            },
                          ],
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Pacientes por nível (top 5)
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <BarChart
                      width={240}
                      height={patientsLevelsChartHeight}
                      layout="horizontal"
                      xAxis={[
                        {
                          label: "Pacientes",
                          min: 0,
                        },
                      ]}
                      yAxis={[
                        {
                          scaleType: "band",
                          data: patientsChartsData.levels.map((l) => l.level),
                        },
                      ]}
                      series={[
                        {
                          data: patientsChartsData.levels.map((l) => l.count),
                          color: "#1976d2",
                        },
                      ]}
                      margin={{ top: 10, right: 10, bottom: 20, left: 60 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          )}

        {reportType === "doctors" && doctorsData && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Médicos distintos
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {doctorsData.totals.doctors}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Consultas no período
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {doctorsData.totals.appointments}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Consultas concluídas
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {doctorsData.totals.completedAppointments}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Horas disponíveis
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {doctorsData.totals.availableHours}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}

        {reportType === "doctors" &&
          doctorsData &&
          doctorsData.doctors.length > 0 && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Concluídas x outras
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <PieChart
                      width={110}
                      height={90}
                      series={[
                        {
                          innerRadius: 18,
                          outerRadius: 40,
                          paddingAngle: 2,
                          data: [
                            {
                              id: 0,
                              value: doctorsChartsData.completedAppointments,
                              label: "Concluídas",
                            },
                            {
                              id: 1,
                              value: doctorsChartsData.pendingOrOtherAppointments,
                              label: "Outras",
                            },
                          ],
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Consultas x horas disponíveis
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <PieChart
                      width={110}
                      height={90}
                      series={[
                        {
                          innerRadius: 18,
                          outerRadius: 40,
                          paddingAngle: 2,
                          data: [
                            {
                              id: 0,
                              value: doctorsChartsData.totalAppointments,
                              label: "Consultas",
                            },
                            {
                              id: 1,
                              value: doctorsChartsData.availableHours,
                              label: "Horas disp.",
                            },
                          ],
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent sx={{ p: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="caption" color="text.secondary">
                    Consultas por médico (top 5)
                  </Typography>
                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                    <BarChart
                      width={240}
                      height={doctorsTopChartHeight}
                      layout="horizontal"
                      xAxis={[
                        {
                          label: "Consultas",
                          min: 0,
                        },
                      ]}
                      yAxis={[
                        {
                          scaleType: "band",
                          data: doctorsChartsData.doctorsByAppointments.map((d) => {
                            const name = d.doctor || "";
                            return name.length > 15 ? `${name.slice(0, 15)}...` : name;
                          }),
                        },
                      ]}
                      series={[
                        {
                          data: doctorsChartsData.doctorsByAppointments.map((d) => d.count),
                          color: "#1976d2",
                        },
                      ]}
                      margin={{ top: 10, right: 10, bottom: 20, left: 80 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          )}

        {/* Tabela de resultados */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Resultado da busca
            </Typography>

            {error && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            {loading && !appointmentsData && !patientsData && !doctorsData && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {reportType === "appointments" && !loading && appointmentsData && appointmentsData.appointments.length === 0 && (
              <Box sx={{ py: 2 }}>
                <Typography color="text.secondary">
                  Nenhum agendamento encontrado para os filtros selecionados.
                </Typography>
              </Box>
            )}

            {reportType === "appointments" &&
              !loading &&
              appointmentsData &&
              appointmentsData.appointments.length > 0 && (
                <>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={orderBy === "date" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "date"}
                            direction={orderBy === "date" ? order : "asc"}
                            onClick={() => handleRequestSort("date")}
                          >
                            Data
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "status" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "status"}
                            direction={orderBy === "status" ? order : "asc"}
                            onClick={() => handleRequestSort("status")}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "patient" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "patient"}
                            direction={orderBy === "patient" ? order : "asc"}
                            onClick={() => handleRequestSort("patient")}
                          >
                            Paciente
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "doctor" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "doctor"}
                            direction={orderBy === "doctor" ? order : "asc"}
                            onClick={() => handleRequestSort("doctor")}
                          >
                            Médico
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "local" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "local"}
                            direction={orderBy === "local" ? order : "asc"}
                            onClick={() => handleRequestSort("local")}
                          >
                            Local
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "city" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "city"}
                            direction={orderBy === "city" ? order : "asc"}
                            onClick={() => handleRequestSort("city")}
                          >
                            Cidade / UF
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedAppointments.map((appt) => (
                        <TableRow key={appt.id} hover>
                          <TableCell>{formatDateTime(appt.date)}</TableCell>
                          <TableCell>
                            <Chip
                              label={appt.status}
                              size="small"
                              color={getStatusColor(appt.status)}
                            />
                          </TableCell>
                          <TableCell>
                            {appt.patient ? (
                              <Button
                                variant="text"
                                size="small"
                                onClick={() =>
                                  navigate(ROUTES.USERINFO.replace(":id", appt.patient!.id))
                                }
                              >
                                {appt.patient.name}
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {appt.doctor ? (
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                  const targetId = appt.doctor!.userId || appt.doctor!.id;
                                  navigate(ROUTES.USERINFO.replace(":id", targetId));
                                }}
                              >
                                {appt.doctor.name}
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>{appt.local?.localName || "-"}</TableCell>
                          <TableCell>
                            {appt.local?.city || "-"}
                            {appt.local?.state ? ` / ${appt.local.state}` : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={appointmentsData.pagination?.totalItems ?? appointmentsData.appointments.length}
                  page={appointmentsPage}
                  onPageChange={handleChangeAppointmentsPage}
                  rowsPerPage={appointmentsRowsPerPage}
                  onRowsPerPageChange={handleChangeAppointmentsRowsPerPage}
                  rowsPerPageOptions={paginationRowsPerPageOptions}
                  labelRowsPerPage="Itens por página:"
                  labelDisplayedRows={getPaginationLabelDisplayedRows}
                />
                </>
              )}

            {reportType === "patients" && !loading && patientsData && patientsData.patients.length > 0 && (
              <>
              <TableContainer component={Paper} sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={orderBy === "patient" ? order : false}>
                        <TableSortLabel
                          active={orderBy === "patient"}
                          direction={orderBy === "patient" ? order : "asc"}
                          onClick={() => handleRequestSort("patient")}
                        >
                          Paciente
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === "date" ? order : false}>
                        <TableSortLabel
                          active={orderBy === "date"}
                          direction={orderBy === "date" ? order : "asc"}
                          onClick={() => handleRequestSort("date")}
                        >
                          Data de cadastro
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === "level" ? order : false}>
                        <TableSortLabel
                          active={orderBy === "level"}
                          direction={orderBy === "level" ? order : "asc"}
                          onClick={() => handleRequestSort("level")}
                        >
                          Nível
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === "socialProgram" ? order : false}>
                        <TableSortLabel
                          active={orderBy === "socialProgram"}
                          direction={orderBy === "socialProgram" ? order : "asc"}
                          onClick={() => handleRequestSort("socialProgram")}
                        >
                          Programa social
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === "city" ? order : false}>
                        <TableSortLabel
                          active={orderBy === "city"}
                          direction={orderBy === "city" ? order : "asc"}
                          onClick={() => handleRequestSort("city")}
                        >
                          Cidade / UF
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === "appointmentsCount" ? order : false}>
                        <TableSortLabel
                          active={orderBy === "appointmentsCount"}
                          direction={orderBy === "appointmentsCount" ? order : "asc"}
                          onClick={() => handleRequestSort("appointmentsCount")}
                        >
                          Consultas
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedPatients.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => navigate(ROUTES.USERINFO.replace(":id", p.id))}
                          >
                            {p.name}
                          </Button>
                        </TableCell>
                        <TableCell>{formatDateTime(p.createdAt)}</TableCell>
                        <TableCell>{p.level ?? "-"}</TableCell>
                        <TableCell>{p.socialProgram ? "Sim" : "Não"}</TableCell>
                        <TableCell>
                          {p.city || p.state
                            ? `${p.city || ""}${p.state ? ` / ${p.state}` : ""}`
                            : "-"}
                        </TableCell>
                        <TableCell>{p.appointmentsCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={patientsData.pagination?.totalItems ?? patientsData.patients.length}
                page={patientsPage}
                onPageChange={handleChangePatientsPage}
                rowsPerPage={patientsRowsPerPage}
                onRowsPerPageChange={handleChangePatientsRowsPerPage}
                rowsPerPageOptions={paginationRowsPerPageOptions}
                labelRowsPerPage="Itens por página:"
                labelDisplayedRows={getPaginationLabelDisplayedRows}
              />
              </>
            )}

            {reportType === "doctors" && !loading && doctorsData && doctorsData.doctors.length > 0 && (
              <>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={orderBy === "doctor" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "doctor"}
                            direction={orderBy === "doctor" ? order : "asc"}
                            onClick={() => handleRequestSort("doctor")}
                          >
                            Médico
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "date" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "date"}
                            direction={orderBy === "date" ? order : "asc"}
                            onClick={() => handleRequestSort("date")}
                          >
                            Data de cadastro
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          Clínicas
                        </TableCell>
                        <TableCell sortDirection={orderBy === "city" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "city"}
                            direction={orderBy === "city" ? order : "asc"}
                            onClick={() => handleRequestSort("city")}
                          >
                            Cidade / UF (principal)
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "appointmentsCount" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "appointmentsCount"}
                            direction={orderBy === "appointmentsCount" ? order : "asc"}
                            onClick={() => handleRequestSort("appointmentsCount")}
                          >
                            Consultas
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "completedAppointmentsCount" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "completedAppointmentsCount"}
                            direction={orderBy === "completedAppointmentsCount" ? order : "asc"}
                            onClick={() => handleRequestSort("completedAppointmentsCount")}
                          >
                            Concluídas
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "availableHours" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "availableHours"}
                            direction={orderBy === "availableHours" ? order : "asc"}
                            onClick={() => handleRequestSort("availableHours")}
                          >
                            Horas disponíveis
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedDoctors.map((d) => {
                        const mainClinic = d.clinics[0];
                        const clinicsLabel = d.clinics.length === 0
                          ? "-"
                          : d.clinics.map((c) => c.localName || c.address || c.id).join(", ");
                        const cityUfLabel = mainClinic
                          ? `${mainClinic.city || ""}${mainClinic.state ? ` / ${mainClinic.state}` : ""}` || "-"
                          : "-";

                        return (
                          <TableRow key={d.id} hover>
                            <TableCell>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => navigate(ROUTES.USERINFO.replace(":id", d.userId))}
                              >
                                {d.name}
                              </Button>
                            </TableCell>
                            <TableCell>{formatDateTime(d.createdAt)}</TableCell>
                            <TableCell>{clinicsLabel}</TableCell>
                            <TableCell>{cityUfLabel}</TableCell>
                            <TableCell>{d.appointmentsCount}</TableCell>
                            <TableCell>{d.completedAppointmentsCount}</TableCell>
                            <TableCell>{d.availableHours}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={doctorsData.pagination?.totalItems ?? doctorsData.doctors.length}
                  page={doctorsPage}
                  onPageChange={handleChangeDoctorsPage}
                  rowsPerPage={doctorsRowsPerPage}
                  onRowsPerPageChange={handleChangeDoctorsRowsPerPage}
                  rowsPerPageOptions={paginationRowsPerPageOptions}
                  labelRowsPerPage="Itens por página:"
                  labelDisplayedRows={getPaginationLabelDisplayedRows}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
